import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import Swal from "sweetalert2";
import Table from "../../core/pagination/datatable";
import { fetchDoctors } from "../../core/redux/slices/doctorSlice";
import { getAppointmentReport } from "../../core/api/reportService";
import { getApiErrorMessage } from "../../core/utils/apiError";

const today = () => new Date().toISOString().split("T")[0];
const DEFAULT_FILTERS = { from: today(), to: today(), doctorId: "", status: "" };

const STATUS_BADGE = { Booked: "primary", Completed: "success", Cancelled: "danger", NoShow: "warning" };

const AppointmentReport = () => {
  const dispatch = useDispatch();
  const doctors = useSelector((s) => s.doctors.list);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const STATUS_OPTIONS = [
    { value: "", label: "All Statuses" },
    { value: "Booked", label: "Booked" },
    { value: "Completed", label: "Completed" },
    { value: "Cancelled", label: "Cancelled" },
    { value: "NoShow", label: "No Show" },
  ];
  const doctorOptions = [{ value: "", label: "All Doctors" }, ...doctors.map((d) => ({ value: d.id, label: d.doctorName }))];

  useEffect(() => { dispatch(fetchDoctors()); }, [dispatch]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { runReport(); }, []);

  const runReport = async (f = filters) => {
    setLoading(true);
    try {
      const res = await getAppointmentReport(f);
      const payload = res.data || {};
      setData(payload.records || payload || []);
      setSummary(payload.summary || null);
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to load report."), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    runReport(DEFAULT_FILTERS);
  };

  const columns = [
    { title: "Date", dataIndex: "appointmentDate", width: 110, render: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—" },
    { title: "Token", dataIndex: "tokenNumber", width: 70, align: "center" },
    { title: "Patient", dataIndex: "patientName" },
    { title: "Doctor", dataIndex: "doctorName" },
    { title: "Procedure", dataIndex: "procedureName" },
    { title: "From", dataIndex: "fromTime", width: 80 },
    { title: "To", dataIndex: "toTime", width: 80 },
    {
      title: "Status", dataIndex: "status", width: 100,
      render: (v) => <span className={`badge bg-${STATUS_BADGE[v] || "secondary"}`}>{v === "NoShow" ? "No Show" : v}</span>,
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title"><h4>Appointment Report</h4><h6>View appointments by date, doctor and status</h6></div>
        </div>
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-auto">
                <label className="form-label small mb-1">From</label>
                <input type="date" className="form-control form-control-sm" value={filters.from}
                  onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} />
              </div>
              <div className="col-auto">
                <label className="form-label small mb-1">To</label>
                <input type="date" className="form-control form-control-sm" value={filters.to}
                  onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} />
              </div>
              <div className="col" style={{ minWidth: 180, maxWidth: 220 }}>
                <label className="form-label small mb-1">Doctor</label>
                <Select classNamePrefix="react-select" options={doctorOptions}
                  value={doctorOptions.find((o) => o.value && o.value === filters.doctorId) || null}
                  placeholder="All Doctors"
                  isClearable
                  onChange={(opt) => setFilters((f) => ({ ...f, doctorId: opt?.value || "" }))} />
              </div>
              <div className="col" style={{ minWidth: 160, maxWidth: 200 }}>
                <label className="form-label small mb-1">Status</label>
                <Select classNamePrefix="react-select" options={STATUS_OPTIONS}
                  value={STATUS_OPTIONS.find((o) => o.value && o.value === filters.status) || null}
                  placeholder="All Statuses"
                  isClearable
                  onChange={(opt) => setFilters((f) => ({ ...f, status: opt?.value || "" }))} />
              </div>
              <div className="col-auto">
                <button className="btn btn-primary" onClick={() => runReport()} disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
              <div className="col-auto">
                <button className="btn btn-dark" onClick={handleReset} disabled={loading}>
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {summary && (
          <div className="row mb-3">
            {[
              { label: "Total", value: summary.total, color: "primary" },
              { label: "Completed", value: summary.completed, color: "success" },
              { label: "Cancelled", value: summary.cancelled, color: "danger" },
              { label: "No Show", value: summary.noShow, color: "warning" },
            ].map((s) => (
              <div key={s.label} className="col-6 col-md-3 mb-2">
                <div className={`card border-${s.color} text-center py-2`}>
                  <div className={`text-${s.color} fs-4 fw-bold`}>{s.value ?? 0}</div>
                  <div className="small text-muted">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-responsive">
              <Table columns={columns} dataSource={data} loading={loading} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentReport;
